<script>
  import { onMount, onDestroy } from "svelte";
  import { NAV } from "../../routes.js";
  import { router, navigate } from "@bitcashier/shared/router/router.svelte.js";
  import { auth, logout } from "../auth/auth.svelte.js";
  import { theme, initTheme, setTheme } from "../theme/theme.svelte.js";
  import { t } from "../i18n/i18n.svelte.js";
  import LanguageSelect from "../i18n/LanguageSelect.svelte";
  import { env } from "../core/env.js";
  import { clients, activeClient, switchClient } from "../clients.svelte.js";
  import { permissions } from "../permissions.svelte.js";
  import { connect, disconnect } from "../ws/marketData.svelte.js";
  import { loadAssets } from "../assets.svelte.js";
  import { balanceSnapshot, loadBalanceSnapshot } from "../balanceSnapshot.svelte.js";
  import { isImpersonatingClient } from "./impersonation.js";
  import { viewingAs } from "../viewAsStore.js";
  import { viewAsEntry } from "../viewAs.svelte.js";
  import { externalUrl } from "../core/url.js";
  import { brand } from "../../brands/index.js";
  import Toggle from "@bitcashier/shared/ui/Toggle.svelte";
  import SearchableSelect from "../ui/SearchableSelect.svelte";
  import TermsGate from "../terms/TermsGate.svelte";
  import SettingsModal from "./SettingsModal.svelte";
  import HeaderBalances from "./HeaderBalances.svelte";
  import ViewAsBanner from "./ViewAsBanner.svelte";

  const envClass = (env.environment || "").toLowerCase();

  let { children } = $props();

  const isActive = (route) =>
    route === "/"
      ? router.path === "/"
      : router.path === route || router.path.startsWith(route + "/");

  // The Auth0 profile picture React rendered in both the header toggle and the profile dropdown
  // (`user.picture`, always present on an Auth0 profile — a Gravatar or generated avatar when the
  // identity provider supplies none). Initials are the fallback for a profile without one, and for
  // an image that fails to load: a broken-image icon in a 26px circle is worse than initials.
  let pictureFailed = $state(false);
  const picture = $derived(pictureFailed ? null : auth.user?.picture);

  // Avatar initials from the signed-in user (name, else email).
  const initials = $derived.by(() => {
    const source = auth.user?.name || auth.user?.email || "?";
    const parts = source.split(/[.\s@]+/).filter(Boolean);
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? "")).toUpperCase() || "?";
  });

  // A Super admin viewing the dashboard as another user (read-only). Fixed for the life of the page:
  // starting and ending a view both reload.
  const viewing = Boolean(viewingAs());
  // Staff acting on a customer's account get a standing warning — see ./impersonation.js for the
  // trigger, which is membership of the internal admin client, NOT the isAdmin permission flag.
  // Not while viewing as someone: the clients are then that user's, nothing can be changed, and the
  // view-as banner already says whose account this is.
  const impersonating = $derived(
    !viewing &&
      isImpersonatingClient(
        clients.items.map((client) => client.id),
        clients.activeId,
      ),
  );
  const showViewAsBanner = $derived(viewing || Boolean(viewAsEntry.error));
  // React measured the banner and offset the layout by its height (store `warningHeight`, read by
  // sidebar.jsx and layout_inner.jsx). Same idea: the banner is sticky under the sticky header, so
  // the sidebar's own sticky offset has to grow by exactly as much.
  let warningHeight = $state(0);

  onMount(() => {
    initTheme(auth.user?.sub);
    connect();
    // React's layout.jsx fetched both per active client for the whole app; the header strip needs
    // the estimated total, and the fiat/coin split needs the asset config. Screens mount before
    // their parent shell, so the guard keeps landing straight on /balances (which loads the same
    // snapshot) to one request; loadAssets() de-duplicates itself.
    loadAssets();
    if (!balanceSnapshot.loading && !balanceSnapshot.loaded) loadBalanceSnapshot();
  });
  onDestroy(disconnect);

  // The header gear, as in React's header.jsx (`props.showSettings`).
  let settingsOpen = $state(false);

  let menuOpen = $state(false);
  let navOpen = $state(false);

  function go(event, route) {
    event.preventDefault();
    navOpen = false;
    navigate(route);
  }
  function goHome(event) {
    event.preventDefault();
    navigate("/");
  }
</script>

<svelte:window onclick={() => (menuOpen = false)} />

<!-- Sits at the top of the left menu, above the nav links, where React put it: layout_inner.jsx
     passed <SelectClient /> as the sidebar's children and sidebar.jsx rendered it above the item
     list. Below 991px the sidebar IS the burger drawer, so the same block covers React's other
     placement (the drawer's profile block, header.jsx:203-208). -->
{#snippet clientSelector()}
  {#if clients.items.length > 1}
    <div class="client-switch">
      <!-- No visible label, as React's sidebar select had none; the name it needs to be announced
           by is carried on the control itself. -->
      <SearchableSelect
        ariaLabel={t("selected_client")}
        value={clients.activeId}
        options={clients.items.map((client) => ({ value: client.id, label: client.name }))}
        onchange={switchClient}
      />
    </div>
  {:else if activeClient()}
    <span class="client-name">{activeClient().name}</span>
  {/if}
{/snippet}

<div
  class="app"
  class:has-warning={impersonating || showViewAsBanner}
  style="--warning-offset: {warningHeight}px"
>
  <header class="app-header">
    <button
      class="burger"
      aria-label="Menu"
      aria-expanded={navOpen}
      onclick={(event) => {
        event.stopPropagation();
        navOpen = !navOpen;
      }}>☰</button
    >
    <a class="brand" href="/" onclick={goHome} title={brand.title}>
      <img src={brand.images.logo} alt={brand.title} />
      <!-- React (header.jsx:124-128, :145-149) showed the environment chip to admin users only —
           `showEnvironmentLabel = isAdmin`, nothing else — so a customer never sees "Sandbox". -->
      {#if env.environment && permissions.isAdmin}
        <span class="env-label env-label--{envClass}">{env.environment}</span>
      {/if}
    </a>

    <!-- Directly after the brand, filling everything up to the account controls: the strip is a
         single line that overflows easily, so it gets the whole middle of the header. The spacer
         after it only matters below 991px, where the slot is hidden and something still has to
         push the gear and the avatar to the right. -->
    <div class="header-balances-slot"><HeaderBalances /></div>

    <div class="spacer"></div>

    <button
      class="gear"
      onclick={() => (settingsOpen = true)}
      aria-label={t("settings")}
      title={t("settings")}>⚙</button
    >

    <div class="user-menu" onclick={(event) => event.stopPropagation()}>
      <button
        class="avatar"
        onclick={() => (menuOpen = !menuOpen)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        {#if picture}
          <img
            class="thumb"
            src={picture}
            alt=""
            referrerpolicy="no-referrer"
            onerror={() => (pictureFailed = true)}
          />
        {:else}
          <span class="initials">{initials}</span>
        {/if}
        <span class="caret">▾</span>
      </button>
      {#if menuOpen}
        <div class="menu" role="menu">
          {#if auth.user}
            <div class="menu-user">
              {#if picture}
                <img class="thumb" src={picture} alt="" referrerpolicy="no-referrer" />
              {:else}
                <span class="initials">{initials}</span>
              {/if}
              <span class="menu-email">{auth.user.email}</span>
            </div>
          {/if}
          <div class="menu-label">{t("change_language")}</div>
          <LanguageSelect />
          <div class="menu-label">{t("theme")}</div>
          <label class="menu-toggle">
            <span>{t("dark_mode")}</span>
            <Toggle
              checked={theme.mode === "dark"}
              onchange={(value) => setTheme(value ? "dark" : "light")}
            />
          </label>
          <hr />
          <button class="menu-item logout" onclick={() => logout()}>{t("logout")}</button>
        </div>
      {/if}
    </div>
  </header>

  <!-- The view-as banner takes the slot of the staff warning below, never both: one standing line
       about whose account this is. The staff warning is a port of
       info_elements/context_warning_info.jsx. The client name is interpolated into the
       translated sentence rather than wrapped in its own element: emphasising it would mean
       splitting the string around the placeholder, which does not survive translation. -->
  {#if showViewAsBanner}
    <div class="banner-slot" bind:clientHeight={warningHeight}><ViewAsBanner /></div>
  {:else if impersonating}
    <div class="context-info" role="status" bind:clientHeight={warningHeight}>
      {t("impersonation_warning", { client: activeClient()?.name ?? "" })}
    </div>
  {/if}

  <div class="shell">
    <aside class="sidebar" class:open={navOpen}>
      <div class="sidebar-client">{@render clientSelector()}</div>
      <!-- Below 991px the header hides the balances (no room), so the drawer carries them — React
           did the same, moving them into its burger menu's profile block (header.jsx:203-208,
           `d-lg-none`). -->
      <div class="drawer-only">
        <HeaderBalances stacked />
      </div>
      <nav>
        {#each NAV as item (item.route)}
          <a
            href={item.route}
            class:active={isActive(item.route)}
            onclick={(event) => go(event, item.route)}
          >
            {t(item.key)}
          </a>
        {/each}
      </nav>
    </aside>

    <div class="main">
      <!-- `content-body` is React's class name, kept deliberately: the Playwright suite waits on
           it, and until the per-env cutover finishes the same specs run against the React build
           (sandbox/prod) and this one (dev). Renaming it would force a spec rewrite that could
           only ever be correct for one of the two stacks. -->
      <div class="content content-body">
        {@render children()}
      </div>
      <!-- React's footer.jsx linked the brand name to its marketing site; the port dropped the
           anchor. `externalUrl` supplies the scheme the brand config omits — React's bare
           `href="www.bitcashier.io"` was a RELATIVE url that navigated inside the dashboard. -->
      <footer class="app-footer">
        © {t("copyright")}
        {new Date().getFullYear()}
        <a href={externalUrl(brand.contact.websiteUrl)} target="_blank" rel="noreferrer noopener"
          >{brand.title}</a
        >
        — {t("all_rights_reserved")}
      </footer>
    </div>
  </div>
</div>

{#if settingsOpen}
  <SettingsModal onClose={() => (settingsOpen = false)} />
{/if}

<!-- Only the user themselves can accept the Terms; while viewing as them the gate would be a wall
     with a button the backend refuses. -->
{#if brand.components.newTermsModal && !viewing}
  <TermsGate />
{/if}

<style>
  .brand {
    display: flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
  }
  .brand img {
    height: 30px;
    width: auto;
    display: block;
  }
  /* Blue default (development), matching React's `$secondary` #26409e. */
  .env-label {
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #fff;
    background-color: #26409e;
  }
  .env-label--sandbox {
    background-color: #c98a12;
  }
  .env-label--production {
    background-color: #d24a4a;
  }

  /* Fills the width of the menu column — the sidebar is too narrow for the side-by-side header
     form this had before. */
  .sidebar-client {
    padding: 12px 12px 0;
  }
  /* The whole gap between the picker and the first menu item, in one place: app.css gives the nav
     8px of its own, which together with this block's old 4px bottom padding read as too loose. */
  .sidebar nav {
    padding-top: 6px;
  }
  /* :global, because the switcher is a SearchableSelect now and scoped styles stop at its
     boundary — without this the box would sit at the app-wide 34px and break the sidebar rhythm. */
  .client-switch :global(input) {
    height: 30px;
  }
  .client-name {
    color: var(--muted);
    font-size: 13px;
    white-space: nowrap;
  }

  /* The banner sits under the sticky header and stays put: a warning about acting on someone
     else's account is worth nothing if it scrolls away. In light mode the colours are React's
     (`.context-info` in src/user/scss/layout/_header.scss): a 30% tint of the brand primary
     behind black text. That light tint would glare against the dark palette, so dark mode wears
     the table header's colours instead — `--header-bg` behind `--muted`, a shade off the page
     background rather than a bright band. */
  .banner-slot {
    position: sticky;
    top: 56px;
    z-index: 19;
  }
  .context-info {
    position: sticky;
    top: 56px;
    z-index: 19;
    padding: 8px 20px;
    background: color-mix(in srgb, var(--accent) 30%, white);
    color: #000;
    font-size: 13px;
    text-align: center;
  }
  :global(:root[data-theme="dark"]) .context-info {
    background: var(--header-bg);
    color: var(--muted);
    border-bottom: 1px solid var(--border);
  }
  @media (prefers-color-scheme: dark) {
    :global(:root:not([data-theme="light"])) .context-info {
      background: var(--header-bg);
      color: var(--muted);
      border-bottom: 1px solid var(--border);
    }
  }

  /* `min-width: 0` is what lets the strip scroll instead of stretching the header: without it the
     slot's flex base is its content's width, so a long balance line pushes the account menu off
     the edge rather than overflowing inside the slot.

     The strip is aligned to the far end of the slot rather than filling it: a short balance line
     then sits next to the account menu, and only a line long enough to need the room reaches back
     to the logo. Aligning the strip (a flex item that shrinks) rather than its contents is what
     keeps that safe — `justify-content: flex-end` inside the scroller itself would push the
     leading balances out of the scrollable range and make them unreachable. */
  .header-balances-slot {
    display: flex;
    justify-content: flex-end;
    flex: 1 1 auto;
    min-width: 0;
  }

  /* Balances live in the burger drawer only; the header carries them above the breakpoint. */
  .drawer-only {
    display: none;
  }

  .burger {
    display: none;
    background: none;
    border: 1px solid var(--border);
    border-radius: 7px;
    color: var(--text);
    font-size: 16px;
    padding: 3px 9px;
    cursor: pointer;
  }

  /* Lives in the always-visible header, so it stays reachable below the 991px breakpoint where
     the sidebar collapses behind the burger — React put a "Settings" item in the burger menu for
     the same reason. */
  .gear {
    background: none;
    border: 1px solid var(--border);
    border-radius: 999px;
    color: var(--muted);
    font-size: 15px;
    line-height: 1;
    padding: 6px 9px;
    cursor: pointer;
  }
  .gear:hover {
    color: var(--accent);
    border-color: var(--accent);
  }

  .user-menu {
    position: relative;
  }
  .avatar {
    display: flex;
    align-items: center;
    gap: 6px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 999px;
    padding: 4px 10px 4px 6px;
    cursor: pointer;
    color: var(--text);
  }
  .initials {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--accent);
    color: #fff;
    display: grid;
    place-items: center;
    font-size: 12px;
    font-weight: 700;
  }
  .caret {
    color: var(--muted);
    font-size: 11px;
  }
  .menu {
    position: absolute;
    right: 0;
    top: calc(100% + 8px);
    min-width: 220px;
    background: var(--card-bg);
    border: 1px solid var(--border);
    border-radius: 10px;
    box-shadow: var(--shadow);
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    z-index: 30;
  }
  /* The avatar, at the same 26px as the header toggle's so the two read as the same person. */
  .thumb {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    object-fit: cover;
    display: block;
    flex: none;
    background: var(--bg);
  }
  .menu-user {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
  }
  .menu-email {
    font-size: 13px;
    color: var(--text);
    word-break: break-all;
  }
  .menu-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--muted);
    padding: 8px 10px 2px;
  }
  .menu-item {
    text-align: left;
    background: none;
    border: none;
    color: var(--text);
    padding: 7px 10px;
    border-radius: 7px;
    cursor: pointer;
    font-size: 13px;
  }
  .menu-item:hover {
    background: var(--row-hover);
  }
  .menu-item.logout {
    color: #d24a4a;
  }
  .menu-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    font-size: 13px;
    color: var(--text);
  }
  .menu hr {
    border: none;
    border-top: 1px solid var(--border);
    margin: 6px 0;
    width: 100%;
  }

  .app-footer {
    margin-top: auto;
    padding: 14px 24px;
    border-top: 1px solid var(--border);
    color: var(--muted);
    font-size: 12px;
    text-align: center;
  }
  .app-footer a {
    color: inherit;
  }

  /* The sticky sidebar starts below the header; when the impersonation banner is up it has to
     start below that too, which is what React's `warningHeight` offset did for the same reason. */
  .app.has-warning :global(.sidebar) {
    top: calc(56px + var(--warning-offset, 0px));
    height: calc(100vh - 56px - var(--warning-offset, 0px));
  }

  /* Below the tablet breakpoint the sidebar collapses behind the burger, as React's
     `d-none d-lg-block` sidebar + burger_menu did. */
  @media (max-width: 991px) {
    .burger {
      display: block;
    }
    /* No room for them in the header; the drawer shows them instead. */
    .header-balances-slot {
      display: none;
    }
    .drawer-only {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 8px 12px 14px;
      border-bottom: 1px solid var(--border);
    }
    :global(.sidebar) {
      display: none;
    }
    :global(.sidebar.open) {
      display: flex;
      position: fixed;
      top: 56px;
      left: 0;
      bottom: 0;
      z-index: 25;
      box-shadow: var(--shadow);
      overflow-y: auto;
    }
    .app.has-warning :global(.sidebar.open) {
      top: calc(56px + var(--warning-offset, 0px));
      height: auto;
    }
  }
</style>
